import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, GripVertical, Edit, ChevronUp, ChevronDown } from 'lucide-react';
import type { FormField, FormFieldType } from '@shared/schema';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select (Dropdown)' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio' },
  { value: 'date', label: 'Date' },
];

interface FormFieldBuilderProps {
  fields: FormField[];
  onFieldsChange: (fields: FormField[]) => void;
}

export function FormFieldBuilder({ fields, onFieldsChange }: FormFieldBuilderProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [fieldForm, setFieldForm] = useState<Partial<FormField>>({
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    options: []
  });
  const [newOption, setNewOption] = useState('');

  const handleAddField = () => {
    setFieldForm({ label: '', type: 'text', required: false, placeholder: '', options: [] });
    setEditingField(null);
    setNewOption('');
    setIsDialogOpen(true);
  };

  const handleEditField = (field: FormField) => {
    setFieldForm(field);
    setEditingField(field);
    setNewOption('');
    setIsDialogOpen(true);
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setFieldForm({
        ...fieldForm,
        options: [...(fieldForm.options || []), newOption.trim()]
      });
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setFieldForm({
      ...fieldForm,
      options: (fieldForm.options || []).filter((_, i) => i !== index)
    });
  };

  const handleOptionKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const handleSaveField = () => {
    if (!fieldForm.label || !fieldForm.type) {
      return;
    }

    // Generate a name from the label (or keep existing name if editing)
    const fieldName = editingField?.name || fieldForm.label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    const newField: FormField = {
      id: editingField?.id || `field-${Date.now()}`,
      name: fieldName,
      label: fieldForm.label,
      type: fieldForm.type as FormFieldType,
      required: fieldForm.required || false,
      placeholder: fieldForm.placeholder,
      options: fieldForm.options || [],
    };

    if (editingField) {
      onFieldsChange(fields.map(f => f.id === editingField.id ? newField : f));
    } else {
      onFieldsChange([...fields, newField]);
    }

    setIsDialogOpen(false);
    setFieldForm({ label: '', type: 'text', required: false, placeholder: '', options: [] });
    setEditingField(null);
  };

  const handleDeleteField = (fieldId: string) => {
    onFieldsChange(fields.filter(f => f.id !== fieldId));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newFields = [...fields];
    [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
    onFieldsChange(newFields);
  };

  const handleMoveDown = (index: number) => {
    if (index === fields.length - 1) return;
    const newFields = [...fields];
    [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    onFieldsChange(newFields);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium">Form Fields</h3>
        <Button type="button" size="sm" onClick={handleAddField}>
          <Plus className="h-4 w-4 mr-2" />
          Add Field
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No fields added yet. Click "Add Field" to get started.
        </p>
      ) : (
        <div className="space-y-2">
          {fields.map((field, index) => (
            <Card key={field.id} className="p-3">
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === fields.length - 1}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{field.label}</span>
                    {field.required && <Badge variant="destructive" className="h-5 text-xs">Required</Badge>}
                    <Badge variant="outline" className="h-5 text-xs">{FIELD_TYPES.find(t => t.value === field.type)?.label}</Badge>
                  </div>
                  {field.placeholder && (
                    <p className="text-xs text-muted-foreground mt-1">Placeholder: {field.placeholder}</p>
                  )}
                  {field.options && field.options.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Options: {field.options.join(', ')}</p>
                  )}
                </div>

                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditField(field)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteField(field.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Field Editor Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingField ? 'Edit Field' : 'Add Field'}</DialogTitle>
            <DialogDescription>
              Configure the field properties
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Field Label *</Label>
              <Input
                value={fieldForm.label || ''}
                onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
                placeholder="e.g., Full Name"
              />
            </div>

            <div className="space-y-2">
              <Label>Field Type *</Label>
              <Select
                value={fieldForm.type}
                onValueChange={(value) => setFieldForm({ ...fieldForm, type: value as FormFieldType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Placeholder Text</Label>
              <Input
                value={fieldForm.placeholder || ''}
                onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })}
                placeholder="e.g., Enter your name"
              />
            </div>

            {(fieldForm.type === 'select' || fieldForm.type === 'radio') && (
              <div className="space-y-2">
                <Label>Options</Label>
                
                {/* Existing options */}
                {fieldForm.options && fieldForm.options.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {fieldForm.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input value={option} readOnly className="flex-1" />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOption(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new option */}
                <div className="flex items-center gap-2">
                  <Input
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={handleOptionKeyPress}
                    placeholder="Enter option and press Add or Enter"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddOption}
                    disabled={!newOption.trim()}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
                
                {(!fieldForm.options || fieldForm.options.length === 0) && (
                  <p className="text-xs text-muted-foreground">No options added yet. Add at least one option above.</p>
                )}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="required"
                checked={fieldForm.required || false}
                onCheckedChange={(checked) => setFieldForm({ ...fieldForm, required: !!checked })}
              />
              <Label htmlFor="required" className="cursor-pointer">
                Required field
              </Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSaveField}>
                {editingField ? 'Update Field' : 'Add Field'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
